/**
 * Header management system that efficiently handles both height tracking and color switching
 * Optimized for performance with single DOM queries, cached elements, and batched updates
 */

// Cached DOM elements and state
let headerElement = null;
let themedModules = null;
let currentHeaderHeight = 0;

// Mobile navigation state
let mobileNavToggle = null;
let headerNav = null;
let mainElement = null;
let footerElement = null;
let isMobileNavOpen = false;
let isDesktopViewport = false;
let mobileNavInitialized = false;

// Dropdown navigation state
let dropdownTriggers = null;
let dropdownPanels = null;
let currentOpenDropdown = null;
let dropdownInitialized = false;

// Batching system
let updateScheduled = false;
let pendingUpdates = {
    headerHeight: false,
    headerColor: false,
    viewport: false
};

// Event listeners for dynamic management
let mobileNavEventListeners = null;
let dropdownEventListeners = null;

/**
 * Initialize header management system
 * Sets up all header-related functionality with shared resources
 */
export function initHeader() {
    // Cache DOM elements once on initialization
    headerElement = document.querySelector('.header');
    themedModules = document.querySelectorAll('[data-theme]');
    
    if (!headerElement) {
        console.warn('Header element not found');
        return;
    }

    // Cache mobile navigation elements
    mobileNavToggle = document.querySelector('.mobile-nav-toggle');
    headerNav = document.querySelector('.header-nav');
    mainElement = document.querySelector('main');
    footerElement = document.querySelector('footer');
    
    // Cache dropdown navigation elements
    dropdownTriggers = document.querySelectorAll('.header-nav-trigger');
    dropdownPanels = document.querySelectorAll('.header-nav-panel');
    
    // Check if mobile navigation can be initialized
    mobileNavInitialized = !!(mobileNavToggle && headerNav);
    if (!mobileNavInitialized) {
        console.warn('Mobile navigation elements not found - mobile nav disabled');
    }

    // Check if dropdown navigation can be initialized
    dropdownInitialized = dropdownTriggers.length > 0 && dropdownPanels.length > 0;
    if (!dropdownInitialized) {
        console.warn('Dropdown navigation elements not found - dropdown nav disabled');
    }

    // Initial setup with consolidated updates
    scheduleUpdate('headerHeight');
    scheduleUpdate('headerColor');
    if (mobileNavInitialized) {
        scheduleUpdate('viewport');
    }

    // Set up efficient observers and listeners
    setupHeightObserver();
    setupScrollListener();
    if (mobileNavInitialized) {
        setupViewportObserver();
    }
    if (dropdownInitialized) {
        setupDropdownListeners();
    }
}

/**
 * Measures the header's current height and sets it as a CSS custom property on the body
 * Uses offsetHeight to include padding and borders in the measurement
 */
function updateHeaderHeight() {
    if (!headerElement) return;
    
    const newHeight = headerElement.offsetHeight;
    
    // Only update if height actually changed (performance optimization)
    if (newHeight !== currentHeaderHeight) {
        currentHeaderHeight = newHeight;
        document.body.style.setProperty('--header-height', `${newHeight}px`);
    }
}

/**
 * Determines which themed module is currently behind the header
 * and updates body classes accordingly for CSS styling
 */
function updateHeaderColor() {
    if (!themedModules.length || currentHeaderHeight === 0) return;
    
    // Loop through cached themed modules to find which one is behind the header
    for (const themedModule of themedModules) {
        // Get module's position relative to viewport
        const rect = themedModule.getBoundingClientRect();
        
        /**
         * Check if this module is currently positioned behind the header:
         * - Module's top edge has reached or passed the bottom of the header
         * - Module's bottom edge is still below the bottom of the header
         * This ensures we only change colors when a module is actually behind the header
         */
        if (rect.top <= currentHeaderHeight && rect.bottom > currentHeaderHeight) {
            // Check the module's theme attribute to determine if it's dark or light
            const isDark = themedModule.dataset.theme === 'dark';
            
            // Update body classes for CSS styling
            // These classes allow CSS to style header content appropriately
            document.body.classList.toggle('header-over-dark', isDark);
            document.body.classList.toggle('header-over-light', !isDark);
            return; // Exit early once we find the relevant module
        }
    }
    
    // Fallback: if no themed module is behind header, default to light theme
    document.body.classList.remove('header-over-dark');
    document.body.classList.add('header-over-light');
}

/**
 * Consolidated batched update function that handles all header-related updates in a single RAF call
 * This reduces the number of animation frames and improves performance
 */
function batchedUpdate() {
    // Process all pending updates in optimal order (reads first, then writes)
    if (pendingUpdates.headerHeight) {
        updateHeaderHeight();
        pendingUpdates.headerHeight = false;
    }
    
    if (pendingUpdates.headerColor) {
        updateHeaderColor();
        pendingUpdates.headerColor = false;
    }
    
    if (pendingUpdates.viewport) {
        updateViewportState();
        pendingUpdates.viewport = false;
    }
    
    updateScheduled = false; // Reset flag for next update cycle
}

/**
 * Schedules a consolidated batched update using requestAnimationFrame
 * Prevents multiple updates from being scheduled in the same frame
 */
function scheduleUpdate(updateType) {
    if (updateType) {
        pendingUpdates[updateType] = true;
    }
    
    if (!updateScheduled) {
        updateScheduled = true;
        requestAnimationFrame(batchedUpdate);
    }
}

/**
 * Set up ResizeObserver to efficiently watch for header size changes
 * This is more performant than listening to window resize events
 * and only triggers when the header itself actually changes size
 */
function setupHeightObserver() {
    const resizeObserver = new ResizeObserver(() => {
        scheduleUpdate('headerHeight');
    });
    
    // Start observing the header element for size changes
    resizeObserver.observe(headerElement);
}

/**
 * Set up optimized scroll listener for header color changes
 * Uses batched updates and RAF throttling for best performance
 */
function setupScrollListener() {
    window.addEventListener('scroll', () => scheduleUpdate('headerColor'), { passive: true });
}

/**
 * Set up ResizeObserver to efficiently watch for viewport changes
 * Uses the observer's contentRect data instead of window.innerWidth
 */
function setupViewportObserver() {
    const resizeObserver = new ResizeObserver((entries) => {
        // Use the observed element's content rect width instead of window.innerWidth
        const entry = entries[0];
        if (entry) {
            const newIsDesktop = entry.contentRect.width >= 1024;
            // Only schedule update if viewport state would actually change
            if (newIsDesktop !== isDesktopViewport) {
                scheduleUpdate('viewport');
            }
        }
    });
    
    // Observe the document element for viewport changes
    resizeObserver.observe(document.documentElement);
}

/**
 * Refresh cached themed modules when DOM changes
 * Call this if modules are added/removed dynamically
 */
export function refreshThemedModules() {
    themedModules = document.querySelectorAll('[data-theme]');
    scheduleUpdate('headerColor');
}

/**
 * Refresh cached dropdown elements when DOM changes
 * Call this if dropdown triggers/panels are added/removed dynamically
 */
export function refreshDropdownElements() {
    dropdownTriggers = document.querySelectorAll('.header-nav-trigger');
    dropdownPanels = document.querySelectorAll('.header-nav-panel');
    
    // Reset dropdown state
    currentOpenDropdown = null;
    
    // Re-check if dropdown navigation can be initialized
    dropdownInitialized = dropdownTriggers.length > 0 && dropdownPanels.length > 0;
    
    if (dropdownInitialized) {
        setupDropdownListeners();
    }
}

/**
 * Set up dropdown navigation event listeners
 * Handles accordion-style dropdowns with proper accessibility
 */
function setupDropdownListeners() {
    const escapeHandler = (event) => {
        if (event.key === 'Escape' && currentOpenDropdown !== null) {
            closeDropdown(currentOpenDropdown);
        }
    };
    
    const clickOutsideHandler = (event) => {
        if (currentOpenDropdown !== null) {
            const openTrigger = dropdownTriggers[currentOpenDropdown];
            const openPanel = dropdownPanels[currentOpenDropdown];
            
            // Close if click is outside both trigger and panel
            if (!openTrigger.contains(event.target) && !openPanel.contains(event.target)) {
                closeDropdown(currentOpenDropdown);
            }
        }
    };
    
    // Add click handlers to each dropdown trigger
    dropdownTriggers.forEach((trigger, index) => {
        const clickHandler = () => {
            toggleDropdown(index);
        };
        trigger.addEventListener('click', clickHandler);
    });
    
    // Add global event listeners
    document.addEventListener('keydown', escapeHandler);
    document.addEventListener('click', clickOutsideHandler);
    
    // Store references for potential cleanup
    dropdownEventListeners = {
        escape: escapeHandler,
        clickOutside: clickOutsideHandler
    };
}

/**
 * Toggle dropdown open/closed state with accordion behavior
 * @param {number} index - Index of the dropdown to toggle
 */
function toggleDropdown(index) {
    if (currentOpenDropdown === index) {
        // Close the currently open dropdown
        closeDropdown(index);
    } else {
        // Close any currently open dropdown first (accordion behavior)
        if (currentOpenDropdown !== null) {
            closeDropdown(currentOpenDropdown);
        }
        // Open the new dropdown
        openDropdown(index);
    }
}

/**
 * Open a specific dropdown
 * @param {number} index - Index of the dropdown to open
 */
function openDropdown(index) {
    if (index < 0 || index >= dropdownTriggers.length) return;
    
    const trigger = dropdownTriggers[index];
    const panel = dropdownPanels[index];
    
    // Update trigger state
    trigger.setAttribute('aria-expanded', 'true');
    
    // Show panel
    panel.removeAttribute('inert');
    
    // Update current state
    currentOpenDropdown = index;
}

/**
 * Close a specific dropdown
 * @param {number} index - Index of the dropdown to close
 */
function closeDropdown(index) {
    if (index < 0 || index >= dropdownTriggers.length) return;
    
    const trigger = dropdownTriggers[index];
    const panel = dropdownPanels[index];
    
    // Update trigger state
    trigger.setAttribute('aria-expanded', 'false');
    
    // Hide panel
    panel.setAttribute('inert', '');
    
    // Clear current state if this was the open dropdown
    if (currentOpenDropdown === index) {
        currentOpenDropdown = null;
    }
}

/**
 * Close all dropdowns
 * Useful for cleanup or when switching contexts
 */
function closeAllDropdowns() {
    if (currentOpenDropdown !== null) {
        closeDropdown(currentOpenDropdown);
    }
}

/**
 * Check viewport width and update mobile navigation state accordingly
 * Uses ResizeObserver data instead of window.innerWidth for efficiency
 */
function updateViewportState() {
    // Use window.innerWidth as fallback (should rarely be needed due to observer optimization)
    const currentIsDesktop = window.innerWidth >= 1024;
    
    // Update viewport state
    isDesktopViewport = currentIsDesktop;
    
    if (isDesktopViewport) {
        // Desktop viewport: clean up mobile navigation state
        if (isMobileNavOpen) {
            // Force close mobile nav without animations
            forceMobileNavClosed();
        }
        
        // Remove inert from navigation for desktop
        headerNav.removeAttribute('inert');
        
        // Remove mobile navigation event listeners
        removeMobileNavEventListeners();
    } else {
        // Mobile viewport: ensure navigation starts in closed state
        headerNav.setAttribute('inert', '');
        
        // Ensure mobile nav is properly closed
        if (isMobileNavOpen) {
            closeMobileNav();
        }
        
        // Add mobile navigation event listeners
        addMobileNavEventListeners();
    }
}

/**
 * Add mobile navigation event listeners dynamically
 * Only active when in mobile viewport
 */
function addMobileNavEventListeners() {
    if (mobileNavEventListeners) return; // Already added
    
    const escapeHandler = (event) => {
        if (event.key === 'Escape' && isMobileNavOpen) {
            closeMobileNav();
        }
    };
    
    const clickOutsideHandler = (event) => {
        if (isMobileNavOpen && 
            !headerNav.contains(event.target) && 
            !mobileNavToggle.contains(event.target)) {
            closeMobileNav();
        }
    };
    
    const toggleHandler = () => {
        toggleMobileNav();
    };
    
    // Add event listeners
    document.addEventListener('keydown', escapeHandler);
    document.addEventListener('click', clickOutsideHandler);
    mobileNavToggle.addEventListener('click', toggleHandler);
    
    // Store references for removal
    mobileNavEventListeners = {
        escape: escapeHandler,
        clickOutside: clickOutsideHandler,
        toggle: toggleHandler
    };
}

/**
 * Remove mobile navigation event listeners dynamically
 * Used when switching to desktop viewport
 */
function removeMobileNavEventListeners() {
    if (!mobileNavEventListeners) return; // Already removed
    
    // Remove event listeners
    document.removeEventListener('keydown', mobileNavEventListeners.escape);
    document.removeEventListener('click', mobileNavEventListeners.clickOutside);
    mobileNavToggle.removeEventListener('click', mobileNavEventListeners.toggle);
    
    // Clear references
    mobileNavEventListeners = null;
}

/**
 * Force mobile navigation to closed state without triggering events
 * Used when switching to desktop viewport
 */
function forceMobileNavClosed() {
    // Reset button state
    mobileNavToggle.setAttribute('aria-expanded', 'false');
    
    // Remove inert from main content and footer
    if (mainElement) {
        mainElement.removeAttribute('inert');
    }
    if (footerElement) {
        footerElement.removeAttribute('inert');
    }
    
    // Remove mobile nav open class from body
    document.body.classList.remove('header-nav-open');
    
    isMobileNavOpen = false;
}

/**
 * Toggle mobile navigation open/closed state
 * Simplified without viewport checks since event listeners are managed dynamically
 */
function toggleMobileNav() {
    if (isMobileNavOpen) {
        closeMobileNav();
    } else {
        openMobileNav();
    }
}

/**
 * Open mobile navigation
 * Sets aria-expanded to true, removes inert from nav, adds inert to main/footer, and adds header-nav-open class
 */
function openMobileNav() {
    // Update button state
    mobileNavToggle.setAttribute('aria-expanded', 'true');
    
    // Make navigation accessible
    headerNav.removeAttribute('inert');
    
    // Make main content and footer inaccessible
    if (mainElement) {
        mainElement.setAttribute('inert', '');
    }
    if (footerElement) {
        footerElement.setAttribute('inert', '');
    }
    
    // Add mobile nav open class to body
    document.body.classList.add('header-nav-open');
    
    isMobileNavOpen = true;
}

/**
 * Close mobile navigation
 * Sets aria-expanded to false, adds inert to nav, removes inert from main/footer, and removes header-nav-open class
 */
function closeMobileNav() {
    // Update button state
    mobileNavToggle.setAttribute('aria-expanded', 'false');
    
    // Make navigation inaccessible
    headerNav.setAttribute('inert', '');
    
    // Make main content and footer accessible again
    if (mainElement) {
        mainElement.removeAttribute('inert');
    }
    if (footerElement) {
        footerElement.removeAttribute('inert');
    }
    
    // Remove mobile nav open class from body
    document.body.classList.remove('header-nav-open');
    
    isMobileNavOpen = false;
}